'use client';

import { useEffect, useMemo, useState } from 'react';
import { NEXT_PROJECT_QUERY, PROJECTS_QUERY_SLUG } from '@/queries/queries';
import { useParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useApolloClient } from '@apollo/client';
import LogoInfo from '@/components/logoInfo';
import GridView from '@/components/projectDetail/grid';
import ListView from '@/components/projectDetail/ListView';
import DetailView from '@/components/projectDetail/DetailView';
import ProjectVideo from '@/components/projectDetail/ProjectVideo';

interface MediaFile {
  url: string;
  alternativeText: string | null;
}

interface Project {
  name: string;
  blocks: any;
  description: {
    children: {
      text: string;
    }[];
  }[];
  year: string;
  awards: string;
  location: string;
  interiors: string;
  children: string;
  about_project?: any;
  label: string;
  value: string;
  media: {
    desktop_media: MediaFile[];
    mobile_media: MediaFile[];
  };
  slug: string;
  related_projects: any;
  video: any;
}

export default function ProjectDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;
  const client = useApolloClient();

  const [project, setProject] = useState<Project | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  const [nextProjectData, setNextProjectData] = useState<Project | null>(null);

  const [activeTab, setActiveTab] = useState<'grid' | 'list' | 'detail' | 'video'>('list');
  const [showSection, setShowSection] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(1);
  const video = project?.video;
  // Hide intro section after 2s
  useEffect(() => {
    const timer = setTimeout(() => setShowSection(false), 2000);
    return () => clearTimeout(timer);
  }, []);
  // Fetch current project by slug
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const { data: currentData } = await client.query({
          query: PROJECTS_QUERY_SLUG,
          variables: { slug },
          fetchPolicy: 'network-only',
        });

        const fetched = currentData?.projects?.[0];
        if (!fetched) return;

        setProject(fetched);
        setCurrentProject(fetched);

        const { data: allProjectsData } = await client.query({
          query: NEXT_PROJECT_QUERY,
          fetchPolicy: 'network-only',
        });

        const allProjects = allProjectsData?.projects || [];
        const currentIndex = allProjects.findIndex((p: any) => p.slug === slug);

        if (currentIndex !== -1 && currentIndex < allProjects.length - 1) {
          setNextProjectData(allProjects[currentIndex + 1]);
        } else {
          setNextProjectData(null);
        }
      } catch (err) {
        console.error('Error loading project and next project:', err);
      }
    };

    if (slug) fetchProject();
  }, [slug, client]);

  // Toggle header/footer visibility
  useEffect(() => {
    const header = document.querySelector('header');
    const footer = document.querySelector('footer');
    if (!header || !footer) return;

    if (showSection) {
      header.classList.add('hidden');
      footer.classList.add('hidden');
    } else if (activeTab === 'list') {
      header.classList.remove('hidden');
      footer.classList.remove('hidden');
    } else {
      header.classList.remove('hidden');
      footer.classList.add('hidden');
    }
  }, [activeTab, showSection]);

  // Prevent scroll when modals are open
  useEffect(() => {
    const body = document.body;
    if (activeTab === 'detail' || activeTab === 'grid' || activeTab === 'video') {
      body.classList.add('overflow-hidden');
    } else {
      body.classList.remove('overflow-hidden');
    }
    return () => {
      body.classList.remove('overflow-hidden');
    };
  }, [activeTab]);

  // Extract all image URLs from blocks
  const getAllImageUrls = (blocks: any[] = []): string[] => {
    const urls: string[] = [];
    blocks.forEach((block) => {
      if (block.__typename === 'ComponentSections2ColumnMedia') {
        block.ColumnMedia?.forEach((mediaItem: any) => {
          mediaItem?.desktop_media?.forEach((img: any) => {
            if (img?.url) urls.push(img.url);
          });
        });
      }
      if (block.__typename === 'ComponentSectionsFullWidthMedia') {
        block.full_widht_media?.desktop_media?.forEach((img: any) => {
          if (img?.url) urls.push(img.url);
        });
      }
    });
    return urls;
  };

  const imageUrls = useMemo(() => getAllImageUrls(project?.blocks), [project?.blocks]);
  const totalSlides = imageUrls.length;

  if (!project) {
    return null;
  }
  return (
    <>
      <AnimatePresence mode="wait">
        {showSection && (
          <motion.section
            key="intro"
            className="relative h-screen bg-[#9E703E] flex items-center overflow-hidden z-[9999]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="container mx-auto px-4">
              <motion.div
                className="relative z-10 flex items-center justify-center w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <h1 className="company-title md:!text-[44px] !text-[#2B332A] leading-none font-light">
                  {project.name}
                </h1>
              </motion.div>
              <LogoInfo width={50} height={70} fillColor="#2B332A" textColor="#2B332A" />
            </div>
          </motion.section>
        )}
      </AnimatePresence>
      {!showSection && (
        <main className="min-h-screen bg-[#E5E1D5] relative">
          <div className="col-span-12 md:px-4 pt-[60px] md:pt-5">
            {currentProject && (
              <ListView
                setActiveTab={setActiveTab}
                project={currentProject}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                totalSlides={totalSlides}
                activeTab={activeTab}
              />
            )}

            <AnimatePresence>
              {activeTab === 'grid' && (
                <motion.div
                  key="grid-modal"
                  className="fixed inset-0 z-[9999] bg-[#E5E1D5] flex items-center justify-center py-4 px-0 menu-related"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full overflow-auto max-h-[100vh]"
                  >
                    <GridView
                      setActiveTab={setActiveTab}
                      project={project}
                      nextProjectData={nextProjectData}
                      imageUrls={imageUrls}
                      currentSlide={currentSlide}
                      setCurrentSlide={setCurrentSlide}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {activeTab === 'detail' && (
                <motion.div
                  key="detail-modal"
                  className="fixed inset-0 z-[9999] flex items-start justify-center md:p-0 project-content menu-related"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full overflow-y-auto max-h-[100vh]"
                  >
                    <DetailView setActiveTab={setActiveTab} project={project} />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {activeTab === 'video' && (
                <motion.div
                  key="detail-modal"
                  className="fixed inset-0 z-[9999] bg-[#E5E1D5] flex items-start justify-center px-0 menu-related video-modal"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full overflow-y-auto max-h-[100vh]"
                  >
                    <ProjectVideo setActiveTab={setActiveTab} project={video} />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      )}
    </>
  );
}
